import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, InputNumber, Switch, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import OpcUaTextField from "./OpcUaTextField";
// INTERFACES
import type {
  OpcUaNamedSubscriptionForm,
  OpcUaSubscriptionConfigForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;

export const BLANK_SUBSCRIPTION_CONFIG: OpcUaSubscriptionConfigForm = {
  publishingIntervalMs: 1000,
  lifetimeCount: 10,
  maxKeepAliveCount: 30,
  maxNotificationsPerPublish: 0,
  priority: 0,
  samplingIntervalMs: 0,
  queueSize: 1,
  publishingEnabled: true,
};

/**
 * Shared field grid for one SubscriptionConfig (used for the connection
 * default, every named subscription, and the "new named subscription" draft).
 *
 * Renders a warning when `lifetimeCount` is under 3x `maxKeepAliveCount` — an
 * OPC UA server is entitled to destroy a subscription that violates that
 * ratio almost immediately, and nothing else in this editor would tell the
 * operator why a subscription they just configured stopped reporting.
 */
function SubscriptionConfigFields({
  config,
  onChangeConfig,
  hideQueueSize = false,
}: {
  config: OpcUaSubscriptionConfigForm;
  onChangeConfig: (config: OpcUaSubscriptionConfigForm) => void;
  /** The "new named subscription" draft shows Queue Size beside its Name field instead. */
  hideQueueSize?: boolean;
}) {
  const [t] = useTranslation("global");
  const onChangeField = <K extends keyof OpcUaSubscriptionConfigForm>(
    field: K,
    value: OpcUaSubscriptionConfigForm[K],
  ) => onChangeConfig({ ...config, [field]: value });

  const showLifetimeWarning = config.lifetimeCount < config.maxKeepAliveCount * 3;

  return (
    <div className="row">
      <div className="col-12 col-md-4 mt-2">
        <OpcUaFieldLabel
          label="Publishing Interval (ms)"
          tooltip={t("general.opcUaPublishingIntervalHelp")}
        />
        <InputNumber
          className="generalStyles__width100"
          size="large"
          style={{ width: "100%", marginTop: 8 }}
          min={0}
          value={config.publishingIntervalMs}
          onChange={(v) => onChangeField("publishingIntervalMs", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-4 mt-2">
        <OpcUaFieldLabel label="Lifetime Count" tooltip={t("general.opcUaLifetimeCountHelp")} />
        <InputNumber
          className="generalStyles__width100"
          size="large"
          style={{ width: "100%", marginTop: 8 }}
          min={0}
          value={config.lifetimeCount}
          onChange={(v) => onChangeField("lifetimeCount", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-4 mt-2">
        <OpcUaFieldLabel
          label="Max Keep-Alive Count"
          tooltip={t("general.opcUaMaxKeepAliveCountHelp")}
        />
        <InputNumber
          className="generalStyles__width100"
          size="large"
          style={{ width: "100%", marginTop: 8 }}
          min={0}
          value={config.maxKeepAliveCount}
          onChange={(v) => onChangeField("maxKeepAliveCount", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-4 mt-2">
        <OpcUaFieldLabel
          label="Max Notifications/Publish"
          tooltip={t("general.opcUaMaxNotificationsPerPublishHelp")}
        />
        <InputNumber
          className="generalStyles__width100"
          size="large"
          style={{ width: "100%", marginTop: 8 }}
          min={0}
          value={config.maxNotificationsPerPublish}
          onChange={(v) => onChangeField("maxNotificationsPerPublish", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-4 mt-2">
        <OpcUaFieldLabel label="Priority" tooltip={t("general.opcUaSubscriptionPriorityHelp")} />
        <InputNumber
          className="generalStyles__width100"
          size="large"
          style={{ width: "100%", marginTop: 8 }}
          min={0}
          max={255}
          value={config.priority}
          onChange={(v) => onChangeField("priority", v ?? 0)}
        />
      </div>
      <div className="col-12 col-md-4 mt-2">
        <OpcUaFieldLabel
          label="Sampling Interval (ms)"
          tooltip={t("general.opcUaSubscriptionSamplingIntervalHelp")}
        />
        <InputNumber
          className="generalStyles__width100"
          size="large"
          style={{ width: "100%", marginTop: 8 }}
          min={0}
          value={config.samplingIntervalMs}
          onChange={(v) => onChangeField("samplingIntervalMs", v ?? 0)}
        />
      </div>
      {!hideQueueSize && (
        <div className="col-12 mt-2">
          <OpcUaFieldLabel
            label="Queue Size"
            tooltip={t("general.opcUaSubscriptionQueueSizeHelp")}
          />
          <InputNumber
            className="generalStyles__width100"
            size="large"
            style={{ width: "100%", marginTop: 8 }}
            min={0}
            value={config.queueSize}
            onChange={(v) => onChangeField("queueSize", v ?? 0)}
          />
        </div>
      )}
      {showLifetimeWarning && (
        <div className="col-12 mt-2">
          <span className="generalStyles__error" style={{ fontSize: "12px" }}>
            {t("general.opcUaLifetimeVsKeepAliveWarning")}
          </span>
        </div>
      )}
      <div className="col-12 mt-2">
        <OpcUaFieldLabel
          label="Publishing Enabled"
          tooltip={t("general.opcUaPublishingEnabledHelp")}
        />
        <div style={{ marginTop: 8 }}>
          <Switch
            checked={config.publishingEnabled}
            onChange={(checked) => onChangeField("publishingEnabled", checked)}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Connection-default subscription settings plus named subscriptions
 * (each referenced by name from a monitor's Subscribe mode).
 */
export default function SubscriptionsEditor({
  subscription,
  onChangeSubscription,
  namedSubscriptions,
  onChangeNamedSubscriptions,
}: {
  subscription: OpcUaSubscriptionConfigForm | null;
  onChangeSubscription: (subscription: OpcUaSubscriptionConfigForm | null) => void;
  namedSubscriptions: OpcUaNamedSubscriptionForm[];
  onChangeNamedSubscriptions: (rows: OpcUaNamedSubscriptionForm[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draftName, setDraftName] = useState("");
  const [draftConfig, setDraftConfig] = useState<OpcUaSubscriptionConfigForm>({
    ...BLANK_SUBSCRIPTION_CONFIG,
  });

  // A Monitor in "Subscribe" mode with a subscriptionName that matches no
  // Named Subscription falls back to this default (see MonitorsEditor / the
  // builder's SubscribeModeT) — so once at least one Named Subscription
  // exists, leaving the default unset silently strands any monitor whose
  // name doesn't match. Lock the toggle on and seed it if it was empty.
  const hasNamedSubscriptions = namedSubscriptions.length > 0;

  useEffect(() => {
    if (hasNamedSubscriptions && subscription === null) {
      onChangeSubscription({ ...BLANK_SUBSCRIPTION_CONFIG });
    }
  }, [hasNamedSubscriptions, subscription, onChangeSubscription]);

  const onToggleDefaultEnabled = (enabled: boolean) =>
    onChangeSubscription(enabled ? { ...BLANK_SUBSCRIPTION_CONFIG } : null);

  const canAddNamed = draftName.trim() !== "";
  const onAddNamed = () => {
    if (!canAddNamed) return;
    onChangeNamedSubscriptions([...namedSubscriptions, { name: draftName, config: draftConfig }]);
    setDraftName("");
    setDraftConfig({ ...BLANK_SUBSCRIPTION_CONFIG });
  };
  const onRemoveNamed = (index: number) => {
    onChangeNamedSubscriptions(namedSubscriptions.filter((_, i) => i !== index));
  };
  const onChangeNamedAt = (index: number, updated: OpcUaNamedSubscriptionForm) => {
    const next = [...namedSubscriptions];
    next[index] = updated;
    onChangeNamedSubscriptions(next);
  };

  return (
    <div className="container mt-3" style={{ width: "100%", maxWidth: "none" }}>
      <div style={{ marginBottom: 8 }}>
        <OpcUaFieldLabel label="Configure connection-default subscription" />
        <div style={{ marginTop: 8 }}>
          <Switch
            checked={subscription !== null}
            onChange={onToggleDefaultEnabled}
            disabled={hasNamedSubscriptions}
          />
        </div>
      </div>
      {hasNamedSubscriptions && (
        <div style={{ marginBottom: 16 }}>
          <span style={{ opacity: 0.7, fontSize: 12 }}>
            {t("general.opcUaSubscriptionDefaultRequiredNote")}
          </span>
        </div>
      )}
      {subscription && (
        <SubscriptionConfigFields config={subscription} onChangeConfig={onChangeSubscription} />
      )}
      <br />
      <Collapse>
        <Panel
          header={t("general.opcUaNamedSubscriptions", { total: namedSubscriptions.length })}
          key="named-subscriptions"
        >
          {namedSubscriptions.map((named, index) => (
            <div
              key={`${named.name}-${index}`}
              className="mb-3"
              style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
            >
              <div className="generalStyles__flex" style={{ justifyContent: "space-between" }}>
                <FormInputText
                  inputValue={named.name}
                  inputLabelName="Name"
                  inputOnChange={(value) => onChangeNamedAt(index, { ...named, name: value })}
                  inputVariableName="name"
                  isRequired
                  disabledInput={false}
                />
                <Tooltip title="Delete">
                  <Button
                    onClick={() => onRemoveNamed(index)}
                    className="buttonStyle__29"
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </div>
              <SubscriptionConfigFields
                config={named.config}
                onChangeConfig={(config) => onChangeNamedAt(index, { ...named, config })}
              />
            </div>
          ))}
          <div className="row">
            <div className="col-12 col-md-6 mt-2">
              <OpcUaTextField
                label="New Subscription Name"
                value={draftName}
                onChange={setDraftName}
                required
              />
            </div>
            <div className="col-12 col-md-6 mt-2">
              <OpcUaFieldLabel
                label="Queue Size"
                tooltip={t("general.opcUaSubscriptionQueueSizeHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={0}
                value={draftConfig.queueSize}
                onChange={(v) => setDraftConfig({ ...draftConfig, queueSize: v ?? 0 })}
              />
            </div>
          </div>
          <SubscriptionConfigFields
            config={draftConfig}
            onChangeConfig={setDraftConfig}
            hideQueueSize
          />
          <div className="row mt-2">
            <div className="col-12">
              <Button
                onClick={onAddNamed}
                disabled={!canAddNamed}
                className={`generalStyles__width100 ${canAddNamed ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
